<template>
  <SettingsCard
    :title="$t('settings.features.widgets.title')"
    :subtitle="$t('settings.features.widgets.subtitle')"
    icon="mdi-widgets-outline"
    :model-value="isOpen"
    @update:modelValue="setOpen"
  >
    <div class="pa-4">
      <v-row>
        <v-col
          cols="12"
          sm="6"
          class="px-0 px-md-2"
        >
          <div class="d-flex flex-column gap-1">
            <span class="font-weight-bold">
              {{ $t('settings.features.widgets.body-texts.0') }}<br />
            </span>
            <span class="text-grey">
              {{ $t('settings.features.widgets.body-texts.1') }}
            </span>
            <ol class="d-flex flex-column gap-1">
              <li class="text-grey">
                <strong>{{ $t('settings.features.widgets.body-texts.2') }}:</strong><br />
                {{ $t('settings.features.widgets.body-texts.3') }}
              </li>
              <li class="text-grey">
                <strong>{{ $t('settings.features.widgets.body-texts.4') }}:</strong><br />
                {{ $t('settings.features.widgets.body-texts.5') }}
              </li>
            </ol>
          </div>
        </v-col>
        <v-col
          cols="12"
          sm="6"
          class="px-0 px-md-2"
        >
          <v-radio-group
            v-if="widgetType"
            :model-value="widgetType"
            :label="$t('settings.features.widgets.labels.mode')"
            class="widgets-settings-radio-group"
            :loading="loading"
            :disabled="loading"
            @update:model-value="saveSettings"
          >
            <v-radio
              color="primary"
              :label="$t('settings.features.widgets.labels.mode-iframe')"
              value="IFRAME"
            />
            <v-radio
              :label="$t('settings.features.widgets.labels.mode-widget')"
              value="WIDGET"
              color="primary"
            />
          </v-radio-group>
          <ProgressCircular v-else />
        </v-col>
      </v-row>

      <v-alert
        v-if="widgetType === 'IFRAME'"
        icon="mdi-alert"
        prominent
        variant="tonal  "
        type="warning"
        class="ma-0 mt-4"
      >
        <p class="ma-0 font-weight-bold">{{ $t('labels.hint') }}:</p>
        <p>
          {{ $t('settings.features.widgets.info-text') }}
        </p>
      </v-alert>
    </div>

    <v-dialog
      v-model="showUpdateWidgetTypeDialog"
      width="700"
    >
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar
            color="primary"
            dark
            flat
          >
            <v-toolbar-title>
              {{ $t('settings.features.widgets.dialog.title') }}
            </v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <!-- TODO: check unsupported prop: dark -->
              <v-btn
                icon
                @click="showUpdateWidgetTypeDialog = false"
              >
                <v-icon size="large"> mdi-close </v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>

        <v-stepper>
          <v-stepper-window v-model="step">
            <v-stepper-items>
              <v-stepper-window-item value="init">
                <v-card-text class="mt-4 text-body-1">
                  <div>
                    <p>{{ $t('settings.features.widgets.info-text') }}</p>
                    {{ $t('settings.features.widgets.dialog.confirm') }}:
                    <v-checkbox
                      color="primary"
                      v-model="confirmWidget"
                      :label="$t('settings.features.widgets.dialog.confirm-checkbox')"
                    />
                  </div>
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    :disabled="!confirmWidget"
                    color="primary"
                    @click="updateWidgetTypeToWidget"
                  >
                    {{ $t('settings.features.widgets.dialog.activate') }}
                  </v-btn>
                </v-card-actions>
              </v-stepper-window-item>

              <v-stepper-window-item value="loading">
                <v-card-text>
                  <ProgressCircular />
                </v-card-text>
              </v-stepper-window-item>

              <v-stepper-window-item value="success">
                <v-card-text class="d-flex flex-column align-center justify-center gap-1">
                  <v-icon
                    color="success"
                    class="mr-4 white"
                    size="150"
                  >
                    mdi-check-circle
                  </v-icon>
                  <p>{{ $t('settings.features.widgets.dialog.success') }}</p>
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="primary"
                    variant="outlined"
                    @click="showUpdateWidgetTypeDialog = false"
                  >
                    {{ $t('labels.close') }}
                  </v-btn>
                </v-card-actions>
              </v-stepper-window-item>
            </v-stepper-items>
          </v-stepper-window>
        </v-stepper>
      </v-card>
    </v-dialog>
  </SettingsCard>
</template>

<script>
import ProgressCircular from '@/components/ProgressCircular.vue'
import SettingsCard from '@/components/SettingsCard.vue'
import { useQueryOpen } from '@/composables/useQueryOpen'
import bus, { eventNames } from '@/lib/eventBus'

import GET_CMS_ORGANIZATION from './queries/GetCmsOrganization.gql'
import UPDATE_WIDGET_TYPE from './queries/UpdateWidgetType.gql'

export default {
  setup() {
    const { isOpen, setOpen } = useQueryOpen({ settingName: 'widgets' })
    return {
      isOpen,
      setOpen,
    }
  },
  components: { SettingsCard, ProgressCircular },
  data() {
    return {
      widgetType: null,
      loading: false,
      showUpdateWidgetTypeDialog: false,
      confirmWidget: false,
      step: 'init',
    }
  },
  apollo: {
    cmsOrganization: {
      query: GET_CMS_ORGANIZATION,
      variables() {
        return {
          companyId: this.$auth.user.companyId,
        }
      },
      update(data) {
        this.widgetType = data.cmsOrganization.widgetType
        return data.cmsOrganization
      },
    },
  },
  watch: {
    showUpdateWidgetTypeDialog(val) {
      // Reset the widgetType when the dialog is closed
      this.widgetType = val ? null : this.cmsOrganization.widgetType
    },
  },
  methods: {
    async saveSettings(widgetType) {
      if (widgetType === 'WIDGET') {
        this.step = 'init'
        this.showUpdateWidgetTypeDialog = true
        this.widgetType = null
      } else {
        this.updateWidgetType(widgetType)
      }
    },
    async updateWidgetTypeToWidget() {
      this.step = 'loading'
      await new Promise((resolve) => setTimeout(resolve, 2000))
      await this.updateWidgetType('WIDGET')
      this.step = 'success'
    },
    async updateWidgetType(widgetType) {
      this.loading = true
      try {
        const { data } = await this.$apollo.mutate({
          mutation: UPDATE_WIDGET_TYPE,
          variables: {
            input: {
              companyId: this.$auth.user.companyId,
              widgetType,
            },
          },
          refetchQueries: [
            { query: GET_CMS_ORGANIZATION, variables: { companyId: this.$auth.user.companyId } },
          ],
        })
        this.widgetType = data.cmsOrganization.widgetType
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('alerts.settings.success'),
        })
      } catch (e) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.settings.features.error'),
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style>
.widgets-settings-radio-group legend {
  white-space: pre-line;
}
</style>
